import { useCallback } from "react";
import { useStore } from "@/app/store";
import { useAppToast } from "@/components/AppToast";
import { saveFailedMessage } from "../components/CostPanel/CostPanel.constants";
import type { AddCostFormValues } from "../components/CostPanel/CostPanel.types";
import type { CategoryGroup } from "./useCalculationDetail";
import type { SaveInputsAndExecuteCallerVariables } from "./useSaveInputsAndExecute";
import useAddCostPanelLifecycle from "./useAddCostPanelLifecycle";
import useActiveBucket from "./useActiveBucket";
import useAddCostFormSeed from "./useAddCostFormSeed";
import useEditDimensionValues from "./useEditDimensionValues";
import { buildSaveInputs } from "./addCostPanel.helpers";

interface UseAddCostPanelArgs {
  categoryGroups: CategoryGroup[];
  pageActiveCategoryId: string;
  pageActiveSubcategoryId: string;
  onConflict: () => void;
  onSave: (variables: SaveInputsAndExecuteCallerVariables) => void;
}

const useAddCostPanel = ({
  categoryGroups,
  pageActiveCategoryId,
  pageActiveSubcategoryId,
  onConflict,
  onSave,
}: UseAddCostPanelArgs) => {
  const { addToast } = useAppToast();
  const lifecycle = useAddCostPanelLifecycle();
  const bucket = useActiveBucket({
    categoryGroups,
    pageActiveCategoryId,
    pageActiveSubcategoryId,
  });
  const seed = useAddCostFormSeed({
    activeCategory: bucket.activeCategory,
    activeDimensionValue: bucket.activeDimensionValue,
  });

  const exitToInputs = lifecycle.exitToInputs;
  const dimensionTypeName = bucket.activeCategory?.dimensionTypeName;
  const handleDimensionValuesSaved = useCallback(() => {
    const message = dimensionTypeName
      ? `${dimensionTypeName} values have been updated.`
      : "Dimension values have been updated.";
    addToast({ appearance: "success", message });
    exitToInputs();
  }, [addToast, dimensionTypeName, exitToInputs]);

  const handleDimensionValuesError = useCallback(() => {
    addToast({ appearance: "danger", message: saveFailedMessage });
  }, [addToast]);

  const dimensionEdit = useEditDimensionValues({
    activeCategory: bucket.activeCategory,
    panelActive: lifecycle.panelActive,
    onConflict,
    onSuccess: handleDimensionValuesSaved,
    onError: handleDimensionValuesError,
    onPendingActiveDimensionValue: bucket.setPendingActiveDimensionValue,
  });

  const openPanel = () => {
    bucket.resetToFirstBucket();
    lifecycle.openPanel();
  };

  const handleToggleFormulaMode = () => {
    lifecycle.toggleFormulaMode();
  };

  const handleEnterEditDimensionValues = () => {
    if (dimensionEdit.enter()) lifecycle.enterEditDimensionValues();
  };

  const handleCancelEditDimensionValues = () => {
    lifecycle.exitToInputs();
  };

  const handleSubmit = (formValues: AddCostFormValues) => {
    const calculationId = useStore.getState().calculation?.id;
    if (!calculationId) return;
    onSave({
      input: {
        calculationId,
        subCategoryId: bucket.activeSubcategory.id,
        inputs: buildSaveInputs(
          formValues,
          seed.formulaVariables,
          seed.planningMonths,
          bucket.activeDimensionValue,
        ),
      },
    });
  };

  return {
    openPanel,
    closePanel: lifecycle.closePanel,
    activeSubcategoryId: bucket.activeSubcategory.id,
    panelActive: lifecycle.panelActive,
    panelProps: {
      active: lifecycle.panelActive,
      onClose: lifecycle.closePanel,
      planningMonths: seed.planningMonths,
      formulaVariables: seed.formulaVariables,
      formulaExpression: seed.formulaExpression,
      sourceTemplateId: seed.sourceTemplateId,
      referencedVariableNames: seed.referencedVariableNames,
      referencedOpDataVariables: seed.referencedOpDataVariables,
      line: seed.line,
      defaultFormValues: seed.defaultFormValues,
      activeSubcategoryName: bucket.activeSubcategory.name,
      activeSubcategoryId: bucket.activeSubcategory.id,
      dimensionValues: bucket.dimensionValues,
      activeDimensionValue: bucket.activeDimensionValue,
      onDimensionValueChange: bucket.setActiveDimensionValue,
      mode: lifecycle.mode,
      onToggleFormulaMode: handleToggleFormulaMode,
      onConflict,
      dimensionEditState: dimensionEdit.dimensionEditState,
      onEnterEditDimensionValues: handleEnterEditDimensionValues,
      onCancelEditDimensionValues: handleCancelEditDimensionValues,
      onSubmitDimensionValues: dimensionEdit.submit,
      isSubmittingDimensionValues: dimensionEdit.isSubmitting,
      onSubmit: handleSubmit,
    },
  };
};

export default useAddCostPanel;
